import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowRight, AlertCircle, Phone, MapPin, Calendar, Quote, ChevronRight, Search, CheckCircle2,
} from "lucide-react";
import { personSchema, breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import { Counter, Reveal, SectionHeading } from "@/components/public/Primitives";
import Timeline from "@/components/public/Timeline";
import TrackWidget from "@/components/public/TrackWidget";
import { ResolvedShareLink } from "@/components/public/ResolvedCardShare";
import { WARD_PRIORITIES, HOODI_MISSION } from "@/data/constants";
import { useLanguage } from "@/context/LanguageContext";
import { getMilestones, getAwards, getSocialWork, getGallery, getResolvedIssues } from "@/lib/api";

const PORTRAIT =
  "https://images.unsplash.com/photo-1625327009034-039a65fa3153?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBpbmRpYW4lMjBtYW4lMjBwb3J0cmFpdCUyMGxlYWRlciUyMG91dGRvb3J8ZW58MHx8fHwxNzgxMTU1NDQ5fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { settings } = useOutletContext();
  const { t, tr, lang } = useLanguage();
  const kn = (item, key) => (lang === "kn" && item[`${key}_kn`]) || item[key];
  const { data: milestones = [] } = useQuery({ queryKey: ["milestones"], queryFn: getMilestones });
  const { data: awards = [] } = useQuery({ queryKey: ["awards"], queryFn: getAwards });
  const { data: posts = [] } = useQuery({ queryKey: ["social-work-home"], queryFn: () => getSocialWork() });
  const { data: gallery = [] } = useQuery({ queryKey: ["gallery"], queryFn: getGallery });
  const { data: resolved = [] } = useQuery({ queryKey: ["resolved-issues"], queryFn: getResolvedIssues });

  const counters = settings?.impactCounters || [];

  return (
    <div data-testid="home-page">
      <PageSeo
        slug="home"
        title="Girish Kumar Naidu | GBA Hoodi Ward 29 Aspirant Candidate"
        description={settings?.heroDescription || "From social service to ward-level civic leadership — committed to making Hoodi Ward 29 cleaner, safer, more responsive, and people-first."}
        keywords="Girish Kumar Naidu, Girish Kumar Naidu Hoodi, Girish Kumar Naidu Ward 29, GBA Hoodi Ward 29, Hoodi Ward 29 aspirant candidate, Hoodi ward development, Hoodi civic issues, Hoodi social worker, Hoodi public service, Greater Bengaluru Authority Hoodi Ward, Bengaluru civic leadership"
        image={settings?.heroImage || PORTRAIT}
        schemas={[
          personSchema(settings),
          { "@context": "https://schema.org", "@type": "WebSite", name: "Girish Kumar Naidu — GBA Hoodi Ward 29", url: window.location.origin },
          { "@context": "https://schema.org", "@type": "Organization", name: "Girish Kumar Naidu Public Service — Hoodi Ward 29", url: window.location.origin, areaServed: "Hoodi Ward 29, Bengaluru, Karnataka, India" },
          breadcrumbSchema([{ name: "Home", url: window.location.origin }]),
        ]}
      />

      {/* ============ HERO ============ */}
      <section className="relative min-h-[94vh] flex items-center bg-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 hero-grid" aria-hidden="true" />
        <p
          className="absolute -right-8 top-24 font-display font-black text-[24rem] leading-none text-stroke select-none pointer-events-none hidden xl:block"
          aria-hidden="true"
        >
          29
        </p>

        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-32 pb-16 w-full">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <motion.p
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2.5 font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] font-bold text-navy mb-7 px-4 py-2 border border-navy/15 rounded-full"
              >
                <span className="h-2 w-2 bg-gold inline-block animate-pulse" /> {tr("home.heroBadge")}
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
                className="font-display text-5xl sm:text-6xl lg:text-[5.5rem] font-black tracking-tighter text-navy leading-[0.95]"
              >
                {(t(settings, "heroTitle") || "Girish Kumar Naidu").split(" ").slice(0, -1).join(" ")}{" "}
                <span className="text-gold">{(t(settings, "heroTitle") || "Girish Kumar Naidu").split(" ").slice(-1)}</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
                className="mt-5 font-mono text-xs md:text-sm uppercase tracking-[0.15em] text-gray-500"
              >
                {t(settings, "heroSubtitle") || "Social Worker | Charitable Trust Leader | Public Service Aspirant"}
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
                className="mt-6 font-display text-2xl md:text-3xl text-navy font-bold tracking-tight"
              >
                {t(settings, "heroTagline") || "Journey of Service. Vision for Hoodi Ward 29."}
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
                className="mt-5 text-gray-600 max-w-xl leading-relaxed text-base md:text-lg"
              >
                {settings ? t(settings, "heroDescription") : ""}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
                className="mt-10 flex flex-wrap items-center gap-3"
              >
                <Link to="/submit-ward-issue" data-testid="hero-submit-issue" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-gold text-white font-bold btn-saffron">
                  <AlertCircle className="h-4 w-4" /> {tr("cta.submitWardIssue")}
                </Link>
                <Link to="/hoodi-ward-29" data-testid="hero-explore-ward" className="inline-flex items-center gap-2 px-7 py-4 rounded-full border-2 border-navy text-navy font-bold hover:bg-navy hover:text-white transition-colors">
                  {tr("cta.exploreWard")} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/track-issue" data-testid="hero-track-issue" className="inline-flex items-center gap-2 px-4 py-4 font-bold text-navy hover:text-gold transition-colors">
                  <Search className="h-4 w-4" /> {tr("cta.trackIssue")}
                </Link>
              </motion.div>
            </div>

            {/* Portrait */}
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
              className="lg:col-span-5 hidden lg:block"
            >
              <div className="relative">
                <div className="absolute -top-5 -right-5 bottom-5 left-5 bg-navy rounded-3xl" aria-hidden="true" />
                <div className="relative rounded-3xl overflow-hidden aspect-[4/5] border border-gray-200 shadow-2xl bg-paper">
                  <img src={settings?.heroImage || PORTRAIT} alt="Girish Kumar Naidu" className="h-full w-full object-cover" />
                  <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-gold text-white font-mono text-[10px] font-bold uppercase tracking-widest shadow-lg">
                    GBA Hoodi Ward 29
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-navy via-navy/75 to-transparent">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Serving Since 2012</p>
                    <p className="text-white font-display text-xl font-bold mt-1">Grassroots. Accessible. Accountable.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ MARQUEE STRIP ============ */}
      <section className="bg-navy py-4 overflow-hidden border-y border-navy" data-testid="priority-marquee">
        <div className="flex gap-10 w-max animate-marquee">
          {[...WARD_PRIORITIES, ...WARD_PRIORITIES].map((p, i) => (
            <span key={`${p.title}-${i}`} className="flex items-center gap-10 font-mono text-xs uppercase tracking-[0.25em] text-slate-300 whitespace-nowrap">
              {kn(p, "title")} <span className="h-1.5 w-1.5 bg-gold inline-block rotate-45" />
            </span>
          ))}
        </div>
      </section>

      {/* ============ IMPACT COUNTERS ============ */}
      <section className="bg-white py-16 md:py-24" data-testid="impact-counters">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 border-t border-gray-200">
            {counters.map((c, i) => (
              <Reveal key={`${c.label}-${i}`} delay={i * 0.08} className={`pt-8 pb-4 px-5 ${i !== 0 ? "md:border-l md:border-gray-200" : ""}`}>
                <p className="font-mono text-xs text-gold font-bold mb-3">{String(i + 1).padStart(2, "0")}</p>
                <p className="font-display text-4xl md:text-5xl font-black text-navy tracking-tight">
                  <Counter value={c.value} suffix={c.suffix || ""} />
                </p>
                <p className="mt-2 font-mono text-[10px] md:text-[11px] text-gray-500 uppercase tracking-[0.15em]">{c.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOODI WARD 29 MISSION ============ */}
      <section className="bg-paper py-20 md:py-32 border-y border-gray-200" data-testid="hoodi-mission-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="inline-flex items-center gap-2.5 font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] font-bold text-gold mb-5">
              <MapPin className="h-3.5 w-3.5" /> {tr("home.missionOverline")}
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-navy leading-[1.02]">
              {tr("home.missionTitle")}
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed">
              {tr("home.missionDesc")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/hoodi-ward-29" data-testid="mission-explore-hoodi" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-navy text-white font-bold hover:bg-navy-700 transition-colors">
                {tr("cta.exploreWard")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/submit-ward-issue" data-testid="mission-submit-issue" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gold text-white font-bold btn-saffron">
                <AlertCircle className="h-4 w-4" /> {tr("cta.submitWardIssue")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {HOODI_MISSION.map((m, i) => (
              <Reveal key={m.title} delay={(i % 2) * 0.06}>
                <div className="group bg-white rounded-2xl p-6 border border-gray-200 card-lift h-full">
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-11 w-11 rounded-xl bg-navy flex items-center justify-center group-hover:bg-gold transition-colors duration-300">
                      <m.icon className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-mono text-[10px] text-gray-300 font-bold">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-display text-base font-bold text-navy leading-snug">{kn(m, "title")}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mt-1.5">{kn(m, "desc")}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TRACK MY ISSUE ============ */}
      <section className="bg-navy py-20 md:py-28 relative grain overflow-hidden" data-testid="track-issue-section">
        <p className="absolute -bottom-16 right-0 font-display font-black text-[16rem] leading-none text-stroke-light select-none pointer-events-none hidden lg:block" aria-hidden="true">
          TRACK
        </p>
        <div className="max-w-4xl mx-auto px-5 md:px-8 relative">
          <SectionHeading
            light
            center
            overline={tr("home.trackOverline")}
            title={tr("home.trackTitle")}
            subtitle={tr("home.trackSubtitle")}
          />
          <TrackWidget />
        </div>
      </section>

      {/* ============ RECENTLY RESOLVED WALL ============ */}
      {resolved.length > 0 && (
        <section className="bg-paper py-20 md:py-28 border-b border-gray-200" data-testid="resolved-wall">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <SectionHeading
              center
              overline={tr("home.resolvedOverline")}
              title={tr("home.resolvedTitle")}
              subtitle={tr("home.resolvedSubtitle")}
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {resolved.slice(0, 6).map((r, i) => (
                <Reveal key={r.referenceId} delay={(i % 3) * 0.07}>
                  <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 card-lift h-full" data-testid={`resolved-card-${i}`}>
                    {r.image && <img src={r.image} alt={r.title} className="h-40 w-full object-cover" />}
                    <div className="p-6">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-civic text-white font-mono text-[10px] font-bold uppercase tracking-wider">
                          <CheckCircle2 className="h-3 w-3" /> Resolved
                        </span>
                        <span className="font-mono text-[10px] text-gray-400">{r.referenceId}</span>
                      </div>
                      <h3 className="font-display text-lg font-bold text-navy leading-snug">{r.title}</h3>
                      <div className="flex items-center gap-3 font-mono text-[11px] text-gray-500 mt-3">
                        <span>{r.category}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {r.locality}</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {(r.updated_at || "").slice(0, 10)}</span>
                      </div>
                      <ResolvedShareLink referenceId={r.referenceId} title={r.title} testId={`resolved-share-${i}`} />
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ MILESTONE ROADMAP ============ */}
      <section className="bg-white py-20 md:py-32" data-testid="roadmap-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading
            center
            overline={tr("home.roadmapOverline")}
            title={tr("home.roadmapTitle")}
            subtitle={tr("home.roadmapSubtitle")}
          />
          <Timeline milestones={milestones} />
        </div>
      </section>

      {/* ============ VISION / WHY ============ */}
      <section className="bg-paper py-20 md:py-32 border-y border-gray-200" data-testid="vision-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="inline-flex items-center gap-2.5 font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] font-bold text-gold mb-5">
              <span className="h-2 w-2 bg-gold inline-block" /> {tr("home.visionOverline")}
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-navy leading-[1.02]">
              {tr("home.visionTitle")}
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed">
              For years, Girish Kumar Naidu has worked with people through charitable and community initiatives.
              His public-service journey is built on accessibility, service, and accountability. As Bengaluru enters
              a new phase of civic governance under the Greater Bengaluru Authority, his mission is to bring
              grassroots problem-solving into ward-level leadership.
            </p>
            <p className="mt-4 text-gray-600 leading-relaxed">
              Roads, drainage, water, waste management, streetlights, public spaces, health, education, and citizen
              accessibility — real civic progress begins at the ward level, close to the people it serves.
            </p>
            <Link to="/gba-corporator-vision" data-testid="vision-learn-more" className="inline-flex items-center gap-2 mt-8 px-6 py-3.5 rounded-full bg-navy text-white font-bold hover:bg-navy-700 transition-colors">
              A Citizen-First Vision <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative rounded-3xl overflow-hidden aspect-square shadow-2xl border border-gray-200">
              <img
                src="https://images.unsplash.com/photo-1771694584049-acf2619bd773?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxiZW5nYWx1cnUlMjBjaXR5JTIwbW9kZXJuJTIwc3RyZWV0fGVufDB8fHx8MTc4MTE1NTQ0OXww&ixlib=rb-4.1.0&q=85"
                alt="Bengaluru city vision"
                className="h-full w-full object-cover"
              />
              <div className="absolute bottom-5 left-5 right-5 bg-white/85 backdrop-blur-xl border border-white/40 rounded-2xl p-5">
                <Quote className="h-6 w-6 text-gold mb-2" />
                <p className="text-navy font-medium leading-relaxed text-sm">
                  "Leadership is not about position — it is about presence, responsibility, and following up until a
                  problem is genuinely solved."
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ SOCIAL WORK HIGHLIGHTS ============ */}
      <section className="bg-white py-20 md:py-32" data-testid="social-work-highlights">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <SectionHeading overline={tr("home.socialOverline")} title={tr("home.socialTitle")} />
            <Link to="/social-work" className="inline-flex items-center gap-2 text-navy font-bold hover:text-gold transition-colors mb-12 md:mb-16" data-testid="view-all-social-work">
              {tr("cta.viewAll")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {posts.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <Link to={`/social-work/${p.slug}`} className="group block bg-white rounded-2xl overflow-hidden border border-gray-200 card-lift h-full">
                  <div className="h-52 overflow-hidden relative">
                    <img src={p.featuredImage} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full font-mono text-[10px] font-bold uppercase tracking-wider bg-gold text-white">{p.category}</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 font-mono text-[11px] text-gray-500 mb-3">
                      <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {p.date}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {p.location}</span>
                    </div>
                    <h3 className="font-display text-lg font-bold text-navy mb-2 group-hover:text-gold transition-colors line-clamp-2">{p.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">{p.shortDescription}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ AWARDS MARQUEE ============ */}
      <section className="bg-paper py-20 md:py-28 border-y border-gray-200 overflow-hidden" data-testid="awards-carousel">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading center overline={tr("home.awardsOverline")} title={tr("home.awardsTitle")} />
        </div>
        <div className="relative">
          <div className="flex gap-6 w-max animate-marquee hover:[animation-play-state:paused]">
            {[...awards, ...awards].map((a, i) => (
              <div key={`${a.id || a.title}-${i}`} className="w-80 shrink-0 bg-white rounded-2xl overflow-hidden border border-gray-200">
                {a.image && <img src={a.image} alt={a.title} className="h-40 w-full object-cover" />}
                <div className="p-6">
                  <span className="font-mono text-lg font-bold text-gold">{a.year}</span>
                  <h3 className="font-display text-lg font-bold text-navy mt-1">{a.title}</h3>
                  <p className="font-mono text-[11px] text-civic mt-1 uppercase tracking-wider">{a.organization}</p>
                  <p className="text-sm text-gray-600 mt-3 line-clamp-2">{a.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-center mt-12">
          <Link to="/awards-recognition" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-navy text-navy font-bold hover:bg-navy hover:text-white transition-colors">
            All Awards <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* ============ GALLERY PREVIEW ============ */}
      <section className="bg-white py-20 md:py-32" data-testid="gallery-preview">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <SectionHeading overline={tr("home.galleryOverline")} title={tr("home.galleryTitle")} />
            <Link to="/gallery" className="inline-flex items-center gap-2 text-navy font-bold hover:text-gold transition-colors mb-12 md:mb-16" data-testid="view-gallery">
              {tr("cta.viewAll")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {gallery.slice(0, 8).map((g, i) => (
              <Reveal key={g.id} delay={(i % 4) * 0.06}>
                <div className="group relative rounded-2xl overflow-hidden aspect-square border border-gray-200">
                  <img src={g.image} alt={g.altText || g.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/85 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                    <p className="text-white text-sm font-semibold">{g.title}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-navy py-20 md:py-32 relative grain overflow-hidden" data-testid="submit-issue-cta">
        <p className="absolute -top-12 left-0 right-0 text-center font-display font-black text-[14rem] leading-none text-stroke-light select-none pointer-events-none hidden lg:block" aria-hidden="true">
          HOODI
        </p>
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center relative">
          <Reveal>
            <p className="inline-flex items-center justify-center gap-2.5 font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] font-bold text-gold mb-6">
              <span className="h-2 w-2 bg-gold inline-block" /> {tr("home.ctaOverline")}
            </p>
            <h2 className="font-display text-4xl md:text-6xl font-black text-white tracking-tighter leading-[0.98]">
              {tr("home.ctaTitle")}
            </h2>
            <p className="mt-6 text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed">
              {tr("home.ctaDesc")}
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link to="/submit-ward-issue" data-testid="cta-submit-issue" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-white font-bold text-lg btn-saffron">
                {tr("cta.submitYourIssue")} <ArrowRight className="h-5 w-5" />
              </Link>
              <a href={`tel:${settings?.phone || ""}`} data-testid="cta-call-office" className="inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white/25 text-white font-bold text-lg hover:border-gold hover:text-gold transition-colors">
                <Phone className="h-5 w-5" /> {settings?.phone || "Call Office"}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
